import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import {
  CloudSync as APIIcon,
  Add as AddIcon,
  Key as KeyIcon,
  Security as SecurityIcon,
  Code as CodeIcon,
  Visibility as ViewIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  CheckCircle as ActiveIcon,
  Error as InactiveIcon
} from '@mui/icons-material';

interface APIManagementProps {
  integrations: any[];
  onRefresh: () => void;
  loading: boolean;
}

const APIManagement: React.FC<APIManagementProps> = ({
  integrations,
  onRefresh,
  loading
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDocumentation, setShowDocumentation] = useState(false);

  // Mock API keys data
  const apiKeys = [
    {
      id: '1',
      name: 'Production API Key',
      key: 'pk_live_51H7...',
      permissions: ['read:invoices', 'write:invoices', 'read:customers'],
      created: '2024-09-01T10:00:00Z',
      last_used: '2024-09-15T09:30:00Z',
      status: 'ACTIVE',
      requests_today: 1247
    },
    {
      id: '2',
      name: 'Development API Key',
      key: 'pk_test_51H7...',
      permissions: ['read:all', 'write:all'],
      created: '2024-08-15T14:30:00Z',
      last_used: '2024-09-14T16:45:00Z',
      status: 'ACTIVE',
      requests_today: 89
    }
  ];

  // Mock API endpoints
  const apiEndpoints = [
    {
      method: 'GET',
      endpoint: '/api/v1/invoices',
      description: 'List all invoices',
      requests_today: 456,
      avg_response_time: '120ms'
    },
    {
      method: 'POST',
      endpoint: '/api/v1/invoices',
      description: 'Create new invoice',
      requests_today: 23,
      avg_response_time: '250ms'
    },
    {
      method: 'GET',
      endpoint: '/api/v1/customers',
      description: 'List all customers',
      requests_today: 234,
      avg_response_time: '95ms'
    },
    {
      method: 'GET',
      endpoint: '/api/v1/expenses',
      description: 'List all expenses',
      requests_today: 167,
      avg_response_time: '110ms'
    }
  ];

  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key);
  };

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'GET': return 'success';
      case 'POST': return 'primary';
      case 'PUT': return 'warning';
      case 'DELETE': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      {/* API Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <APIIcon color="primary" />
                <Box>
                  <Typography variant="body2" color="textSecondary">API Keys</Typography>
                  <Typography variant="h6">{apiKeys.length}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <CodeIcon color="success" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Endpoints</Typography>
                  <Typography variant="h6">{apiEndpoints.length}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <RefreshIcon color="info" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Requests Today</Typography>
                  <Typography variant="h6">
                    {apiKeys.reduce((sum, key) => sum + key.requests_today, 0).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <SecurityIcon color="warning" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Security</Typography>
                  <Typography variant="h6" color="success.main">Secure</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* API Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
            <Tab label="API Keys" />
            <Tab label="Endpoints" />
            <Tab label="Documentation" />
          </Tabs>
        </Box>

        <CardContent>
          {/* API Keys Tab */}
          {activeTab === 0 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">API Keys</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowCreateDialog(true)}
                >
                  Create API Key
                </Button>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Security Notice:</strong> API keys provide access to your financial data. 
                  Keep them secure and rotate them regularly.
                </Typography>
              </Alert>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Key</TableCell>
                      <TableCell>Permissions</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Last Used</TableCell>
                      <TableCell align="right">Requests Today</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {apiKeys.map((key) => (
                      <TableRow key={key.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {key.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" fontFamily="monospace">
                              {key.key}
                            </Typography>
                            <Tooltip title="Copy API Key">
                              <IconButton size="small" onClick={() => handleCopyKey(key.key)}>
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {key.permissions.slice(0, 2).map((permission, index) => (
                              <Chip key={index} label={permission} size="small" />
                            ))}
                            {key.permissions.length > 2 && (
                              <Chip label={`+${key.permissions.length - 2} more`} size="small" variant="outlined" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(key.created).toLocaleDateString('en-GB')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(key.last_used).toLocaleDateString('en-GB')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {key.requests_today.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={key.status === 'ACTIVE' ? <ActiveIcon /> : <InactiveIcon />}
                            label={key.status}
                            color={key.status === 'ACTIVE' ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small">
                            <ViewIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Endpoints Tab */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>API Endpoints</Typography>
              
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Method</TableCell>
                      <TableCell>Endpoint</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Requests Today</TableCell>
                      <TableCell align="right">Avg Response Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {apiEndpoints.map((endpoint, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Chip
                            label={endpoint.method}
                            color={getMethodColor(endpoint.method) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {endpoint.endpoint}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {endpoint.description}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {endpoint.requests_today.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {endpoint.avg_response_time}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Documentation Tab */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>API Documentation</Typography>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  Complete API documentation with examples and authentication details.
                </Typography>
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Authentication</Typography>
                      <Typography variant="body2" paragraph>
                        Use your API key in the Authorization header:
                      </Typography>
                      <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace' }}>
                        Authorization: Bearer YOUR_API_KEY
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Base URL</Typography>
                      <Typography variant="body2" paragraph>
                        All API requests should be made to:
                      </Typography>
                      <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace' }}>
                        https://api.yourfinancialapp.com/v1
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Example Request</Typography>
                      <Typography variant="body2" paragraph>
                        Get all invoices:
                      </Typography>
                      <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {`curl -X GET "https://api.yourfinancialapp.com/v1/invoices" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"`}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Create API Key Dialog */}
      <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <KeyIcon />
            Create API Key
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Key Name"
            placeholder="e.g., Production API Key"
            margin="normal"
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Permissions</InputLabel>
            <Select multiple value={[]} label="Permissions">
              <MenuItem value="read:invoices">Read Invoices</MenuItem>
              <MenuItem value="write:invoices">Write Invoices</MenuItem>
              <MenuItem value="read:customers">Read Customers</MenuItem>
              <MenuItem value="write:customers">Write Customers</MenuItem>
              <MenuItem value="read:expenses">Read Expenses</MenuItem>
              <MenuItem value="write:expenses">Write Expenses</MenuItem>
            </Select>
          </FormControl>
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Important:</strong> Copy your API key immediately after creation. 
              You won't be able to see it again for security reasons.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
          <Button variant="contained">Create Key</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default APIManagement;
